import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Plus, X, Trash2, Pencil, Upload } from "lucide-react";

function flagFromCode(code) {
  if (!code || code.length !== 2) return "🌍";
  const cc = code.toUpperCase();
  return String.fromCodePoint(...[...cc].map((c) => 127397 + c.charCodeAt(0)));
}

const emptyForm = {
  name: "",
  title: "",
  photo_url: "",
  country: "",
  country_code: "",
  city: "",
  services: "",
  bio: "",
  languages: "",
  industries: "",
  years_experience: 0,
  email: "",
  linkedin: "",
  is_active: true,
  sort_order: 0,
};

export default function AdminPartners() {
  const { t, lang } = useI18n();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [err, setErr] = useState("");

  const load = async () => {
    const { data } = await api.get("/admin/partners");
    setList(data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); setErr(""); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      ...emptyForm,
      ...p,
      services: (p.services || []).join(", "),
      languages: (p.languages || []).join(", "),
      industries: (p.industries || []).join(", "),
      years_experience: p.years_experience ?? 0,
    });
    setOpen(true); setErr("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const payload = {
        ...form,
        services: form.services.split(",").map((s) => s.trim()).filter(Boolean),
        languages: form.languages.split(",").map((s) => s.trim()).filter(Boolean),
        industries: form.industries.split(",").map((s) => s.trim()).filter(Boolean),
        years_experience: parseInt(form.years_experience) || 0,
        sort_order: parseInt(form.sort_order) || 0,
        country_code: (form.country_code || "").toUpperCase(),
      };
      if (editing) await api.patch(`/admin/partners/${editing.id}`, payload);
      else await api.post("/admin/partners", payload);
      setOpen(false);
      load();
    } catch (e) {
      setErr(e.response?.data?.detail || "Error");
    }
  };

  const del = async (id) => {
    if (!window.confirm(lang === "el" ? "Διαγραφή συνεργάτη;" : "Delete partner?")) return;
    await api.delete(`/admin/partners/${id}`); load();
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post("/files/upload-public", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setForm((f) => ({ ...f, photo_url: data.url }));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Super Admin</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight mt-1">
            {lang === "el" ? "Διαχείριση Συνεργατών" : "Partner Management"}
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            {lang === "el"
              ? "Αυτοί οι συνεργάτες εμφανίζονται στη δημόσια σελίδα /partners της αρχικής."
              : "These partners appear on the public /partners page of the homepage."}
          </p>
        </div>
        <button onClick={openCreate} data-testid="partner-create-button" className="inline-flex items-center gap-2 px-4 py-2 bg-[#0052CC] text-white rounded-md text-sm font-semibold hover:bg-[#0043A8]">
          <Plus className="w-4 h-4" /> {lang === "el" ? "Νέος συνεργάτης" : "New partner"}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-partners-grid">
        {list.length === 0 ? (
          <div className="col-span-full text-center text-slate-400 py-16 bg-white border border-slate-200 rounded-lg">
            {t("common.no_data")}
          </div>
        ) : list.map((p) => (
          <div key={p.id} className="bg-white border border-slate-200 rounded-lg p-5 hover:-translate-y-0.5 hover:shadow-sm transition-all" data-testid={`admin-partner-${p.id}`}>
            <div className="flex items-start gap-4">
              <div className="relative w-16 h-16 rounded-full bg-slate-100 overflow-hidden flex-shrink-0">
                {p.photo_url ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${p.photo_url}`} alt={p.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-400 font-semibold text-xl">{p.name?.[0]}</div>
                )}
                <span className="absolute -bottom-1 -right-1 text-xl">{flagFromCode(p.country_code)}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-heading font-semibold text-slate-900 truncate">{p.name}</h3>
                  <div className="flex">
                    <button onClick={() => openEdit(p)} className="p-1.5 text-slate-500 hover:text-[#0052CC]" data-testid={`partner-edit-${p.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => del(p.id)} className="p-1.5 text-slate-500 hover:text-red-600" data-testid={`partner-delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                {p.title && <div className="text-sm text-slate-600 truncate">{p.title}</div>}
                <div className="text-xs text-slate-500 mt-0.5">{p.city ? `${p.city}, ` : ""}{p.country}</div>
              </div>
            </div>
            {p.bio && <p className="mt-3 text-sm text-slate-600 line-clamp-2">{p.bio}</p>}
            {p.services?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1">
                {p.services.slice(0, 3).map((s) => (
                  <span key={s} className="text-[10px] px-2 py-0.5 bg-[#0052CC]/10 text-[#0052CC] rounded font-semibold">{s}</span>
                ))}
              </div>
            )}
            <div className="mt-3 flex items-center justify-between text-xs">
              <span className={`px-2 py-0.5 rounded font-semibold ${p.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                {p.is_active ? (lang === "el" ? "Ενεργός" : "Active") : (lang === "el" ? "Ανενεργός" : "Inactive")}
              </span>
              <span className="text-slate-400">#{p.sort_order || 0}</span>
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="font-heading font-semibold text-slate-900">
                {editing ? (lang === "el" ? "Επεξεργασία συνεργάτη" : "Edit partner") : (lang === "el" ? "Νέος συνεργάτης" : "New partner")}
              </h3>
              <button onClick={() => setOpen(false)}><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="px-6 py-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="partner-form">
              <Inp label={lang === "el" ? "Όνομα" : "Name"} testid="partner-name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <Inp label={lang === "el" ? "Τίτλος / Ρόλος" : "Title / Role"} testid="partner-title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
              <Inp label={lang === "el" ? "Χώρα" : "Country"} testid="partner-country" required value={form.country} onChange={(v) => setForm({ ...form, country: v })} />
              <Inp label={lang === "el" ? "ISO κωδικός χώρας (π.χ. DE, IT)" : "Country code ISO2 (e.g. DE)"} testid="partner-country-code" value={form.country_code} onChange={(v) => setForm({ ...form, country_code: v.toUpperCase().slice(0, 2) })} />
              <Inp label={lang === "el" ? "Πόλη" : "City"} testid="partner-city" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
              <Inp label={lang === "el" ? "Χρόνια εμπειρίας" : "Years experience"} testid="partner-exp" type="number" value={form.years_experience} onChange={(v) => setForm({ ...form, years_experience: v })} />
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">
                  {lang === "el" ? "Σύντομο βιογραφικό" : "Short bio"}
                </label>
                <textarea data-testid="partner-bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={4} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
              </div>
              <Inp label={lang === "el" ? "Υπηρεσίες (διαχωρισμένες με κόμμα)" : "Services (comma-separated)"} testid="partner-services" value={form.services} onChange={(v) => setForm({ ...form, services: v })} />
              <Inp label={lang === "el" ? "Γλώσσες (κόμμα)" : "Languages (comma)"} testid="partner-languages" value={form.languages} onChange={(v) => setForm({ ...form, languages: v })} />
              <Inp label={lang === "el" ? "Κλάδοι (κόμμα)" : "Industries (comma)"} testid="partner-industries" value={form.industries} onChange={(v) => setForm({ ...form, industries: v })} />
              <Inp label="Email" testid="partner-email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
              <Inp label="LinkedIn URL" testid="partner-linkedin" value={form.linkedin} onChange={(v) => setForm({ ...form, linkedin: v })} />
              <Inp label={lang === "el" ? "Σειρά εμφάνισης" : "Sort order"} testid="partner-sort" type="number" value={form.sort_order} onChange={(v) => setForm({ ...form, sort_order: v })} />

              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">
                  {lang === "el" ? "Φωτογραφία" : "Photo"}
                </label>
                <div className="mt-1.5 flex items-center gap-3">
                  {form.photo_url ? (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${form.photo_url}`} alt="" className="w-16 h-16 rounded-full object-cover border border-slate-200" />
                  ) : (
                    <div className="w-16 h-16 border border-dashed border-slate-300 rounded-full" />
                  )}
                  <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-2 border border-dashed border-slate-300 rounded-md text-sm text-slate-600 hover:border-[#0052CC]">
                    <Upload className="w-4 h-4" /> {lang === "el" ? "Ανέβασμα" : "Upload"}
                    <input data-testid="partner-photo-upload" type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
                  </label>
                </div>
              </div>

              <div className="sm:col-span-2 flex items-center gap-2">
                <input id="is_active" type="checkbox" checked={!!form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="partner-is-active" />
                <label htmlFor="is_active" className="text-sm text-slate-700">
                  {lang === "el" ? "Ορατός στη δημόσια σελίδα" : "Visible on public page"}
                </label>
              </div>

              {err && <div className="sm:col-span-2 text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>}

              <div className="sm:col-span-2 flex justify-end gap-2 pt-2 border-t border-slate-200 mt-2">
                <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 border border-slate-200 rounded-md text-sm font-medium">{t("common.cancel")}</button>
                <button type="submit" data-testid="partner-submit" className="px-4 py-2 bg-[#0052CC] text-white rounded-md text-sm font-semibold">{t("common.save")}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Inp({ label, testid, value, onChange, type = "text", required }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">{label}</label>
      <input data-testid={testid} type={type} required={required} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
    </div>
  );
}
